import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Shield, Loader2, CheckCircle, Eye, EyeOff } from "lucide-react";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await API.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center p-6 font-nunito">
        <div className="text-center">
          <h2 className="text-2xl font-extrabold text-[#1F1F2E] mb-4">Invalid reset link</h2>
          <Link to="/forgot-password" className="text-[#FF5C39] font-bold hover:underline">Request a new one</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center p-6 font-nunito" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#FF5C39] rounded-2xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" strokeWidth={3} />
          </div>
          <span className="text-2xl font-extrabold text-[#1F1F2E]">Alfie</span>
        </div>

        {done ? (
          <div className="bg-white rounded-[32px] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50 text-center">
            <CheckCircle className="w-16 h-16 text-[#34D399] mx-auto mb-4" strokeWidth={2} />
            <h2 className="text-2xl font-extrabold text-[#1F1F2E] mb-2" data-testid="reset-success-heading">Password updated!</h2>
            <p className="text-[#52526B] font-semibold mb-6">You can now sign in with your new password.</p>
            <Link
              to="/login"
              data-testid="go-to-login-link"
              className="inline-block bg-[#FF5C39] text-white font-bold rounded-2xl px-8 py-3 hover:bg-[#E04828] transition-all"
            >
              Sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-extrabold text-[#1F1F2E] tracking-tight" data-testid="reset-password-heading">
              Choose a new password
            </h1>
            <p className="text-[#52526B] font-semibold mt-2 mb-6">Make it something you'll remember!</p>

            {error && (
              <div className="bg-[#F43F5E]/10 text-[#F43F5E] px-4 py-3 rounded-2xl font-bold text-sm mb-4" data-testid="reset-password-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1.5 block">New password</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                    className="w-full bg-[#FFF0E0]/50 border-2 border-transparent focus:border-[#4D96FF] focus:bg-white rounded-2xl px-4 py-3 pr-12 text-base font-semibold outline-none transition-all placeholder:text-[#52526B]/50"
                    placeholder="Min 6 characters"
                    required
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52526B]">
                    {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="reset-password-submit-button"
                className="w-full bg-[#FF5C39] text-white font-bold rounded-2xl px-6 py-4 hover:bg-[#E04828] active:scale-95 transition-all shadow-[0_8px_20px_rgba(255,92,57,0.25)] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                Reset password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
